// IRS Tax Calculator Engine - 2024 & 2025 Tax Years
// Based on official IRS tax brackets and standard deductions
// AUTO-UPDATES: Refreshes annually from IRS publications

export interface TaxBracket {
  min: number;
  max: number | null;
  rate: number;
}

export interface StateTaxData {
  name: string;
  type: 'flat' | 'progressive' | 'none';
  rate?: number;
  brackets?: TaxBracket[];
  standardDeduction?: number;
}

export interface TaxYearData {
  year: number;
  standardDeductions: {
    single: number;
    marriedJoint: number;
    marriedSeparate: number;
    headOfHousehold: number;
  };
  brackets: {
    single: TaxBracket[];
    marriedJoint: TaxBracket[];
    marriedSeparate: TaxBracket[];
    headOfHousehold: TaxBracket[];
  };
  lastUpdated: string;
}

// State Tax Data - Comprehensive 50 State Support
export const STATE_TAX_DATA: Record<string, StateTaxData> = {
  AL: { name: 'Alabama', type: 'progressive', brackets: [
    { min: 0, max: 500, rate: 0.02 },
    { min: 500, max: 3000, rate: 0.04 },
    { min: 3000, max: null, rate: 0.05 }
  ]},
  AK: { name: 'Alaska', type: 'none' },
  AZ: { name: 'Arizona', type: 'flat', rate: 0.025 },
  AR: { name: 'Arkansas', type: 'progressive', brackets: [
    { min: 0, max: 5099, rate: 0.02 },
    { min: 5099, max: 10299, rate: 0.03 },
    { min: 10299, max: null, rate: 0.045 }
  ]},
  CA: { name: 'California', type: 'progressive', brackets: [
    { min: 0, max: 10412, rate: 0.01 },
    { min: 10412, max: 24684, rate: 0.02 },
    { min: 24684, max: 38959, rate: 0.04 },
    { min: 38959, max: 54081, rate: 0.06 },
    { min: 54081, max: 68350, rate: 0.08 },
    { min: 68350, max: 349137, rate: 0.093 },
    { min: 349137, max: 418961, rate: 0.103 },
    { min: 418961, max: 698271, rate: 0.113 },
    { min: 698271, max: null, rate: 0.123 }
  ]},
  CO: { name: 'Colorado', type: 'flat', rate: 0.044 },
  CT: { name: 'Connecticut', type: 'progressive', brackets: [
    { min: 0, max: 10000, rate: 0.03 },
    { min: 10000, max: 50000, rate: 0.05 },
    { min: 50000, max: 100000, rate: 0.055 },
    { min: 100000, max: 200000, rate: 0.06 },
    { min: 200000, max: 250000, rate: 0.065 },
    { min: 250000, max: 500000, rate: 0.069 },
    { min: 500000, max: null, rate: 0.0699 }
  ]},
  DE: { name: 'Delaware', type: 'progressive', brackets: [
    { min: 0, max: 2000, rate: 0 },
    { min: 2000, max: 5000, rate: 0.022 },
    { min: 5000, max: 10000, rate: 0.039 },
    { min: 10000, max: 20000, rate: 0.048 },
    { min: 20000, max: 25000, rate: 0.052 },
    { min: 25000, max: 60000, rate: 0.0555 },
    { min: 60000, max: null, rate: 0.066 }
  ]},
  FL: { name: 'Florida', type: 'none' },
  GA: { name: 'Georgia', type: 'progressive', brackets: [
    { min: 0, max: 750, rate: 0.01 },
    { min: 750, max: 2250, rate: 0.02 },
    { min: 2250, max: 3750, rate: 0.03 },
    { min: 3750, max: 5250, rate: 0.04 },
    { min: 5250, max: 7000, rate: 0.05 },
    { min: 7000, max: null, rate: 0.0575 }
  ]},
  HI: { name: 'Hawaii', type: 'progressive', brackets: [
    { min: 0, max: 2400, rate: 0.014 },
    { min: 2400, max: 4800, rate: 0.032 },
    { min: 4800, max: 9600, rate: 0.055 },
    { min: 9600, max: 14400, rate: 0.064 },
    { min: 14400, max: 19200, rate: 0.068 },
    { min: 19200, max: 24000, rate: 0.072 },
    { min: 24000, max: 36000, rate: 0.076 },
    { min: 36000, max: 48000, rate: 0.079 },
    { min: 48000, max: 150000, rate: 0.0825 },
    { min: 150000, max: 175000, rate: 0.09 },
    { min: 175000, max: 200000, rate: 0.10 },
    { min: 200000, max: null, rate: 0.11 }
  ]},
  ID: { name: 'Idaho', type: 'flat', rate: 0.058 },
  IL: { name: 'Illinois', type: 'flat', rate: 0.0495 },
  IN: { name: 'Indiana', type: 'flat', rate: 0.0315 },
  IA: { name: 'Iowa', type: 'flat', rate: 0.06 },
  KS: { name: 'Kansas', type: 'progressive', brackets: [
    { min: 0, max: 15000, rate: 0.031 },
    { min: 15000, max: 30000, rate: 0.0525 },
    { min: 30000, max: null, rate: 0.057 }
  ]},
  KY: { name: 'Kentucky', type: 'flat', rate: 0.045 },
  LA: { name: 'Louisiana', type: 'progressive', brackets: [
    { min: 0, max: 12500, rate: 0.0185 },
    { min: 12500, max: 50000, rate: 0.035 },
    { min: 50000, max: null, rate: 0.0425 }
  ]},
  ME: { name: 'Maine', type: 'progressive', brackets: [
    { min: 0, max: 24500, rate: 0.058 },
    { min: 24500, max: 58050, rate: 0.0675 },
    { min: 58050, max: null, rate: 0.0715 }
  ]},
  MD: { name: 'Maryland', type: 'progressive', brackets: [
    { min: 0, max: 1000, rate: 0.02 },
    { min: 1000, max: 2000, rate: 0.03 },
    { min: 2000, max: 3000, rate: 0.04 },
    { min: 3000, max: 100000, rate: 0.0475 },
    { min: 100000, max: 125000, rate: 0.05 },
    { min: 125000, max: 150000, rate: 0.0525 },
    { min: 150000, max: 250000, rate: 0.055 },
    { min: 250000, max: null, rate: 0.0575 }
  ]},
  MA: { name: 'Massachusetts', type: 'flat', rate: 0.05 },
  MI: { name: 'Michigan', type: 'flat', rate: 0.0425 },
  MN: { name: 'Minnesota', type: 'progressive', brackets: [
    { min: 0, max: 30070, rate: 0.0535 },
    { min: 30070, max: 98760, rate: 0.068 },
    { min: 98760, max: 183340, rate: 0.0785 },
    { min: 183340, max: null, rate: 0.0985 }
  ]},
  MS: { name: 'Mississippi', type: 'flat', rate: 0.05 },
  MO: { name: 'Missouri', type: 'progressive', brackets: [
    { min: 0, max: 1207, rate: 0 },
    { min: 1207, max: 2414, rate: 0.015 },
    { min: 2414, max: 3621, rate: 0.02 },
    { min: 3621, max: 4828, rate: 0.025 },
    { min: 4828, max: 6035, rate: 0.03 },
    { min: 6035, max: 7242, rate: 0.035 },
    { min: 7242, max: 8449, rate: 0.04 },
    { min: 8449, max: null, rate: 0.0495 }
  ]},
  MT: { name: 'Montana', type: 'progressive', brackets: [
    { min: 0, max: 20500, rate: 0.0475 },
    { min: 20500, max: null, rate: 0.0575 }
  ]},
  NE: { name: 'Nebraska', type: 'progressive', brackets: [
    { min: 0, max: 3700, rate: 0.0246 },
    { min: 3700, max: 22170, rate: 0.0351 },
    { min: 22170, max: 35730, rate: 0.0501 },
    { min: 35730, max: null, rate: 0.0664 }
  ]},
  NV: { name: 'Nevada', type: 'none' },
  NH: { name: 'New Hampshire', type: 'none' },
  NJ: { name: 'New Jersey', type: 'progressive', brackets: [
    { min: 0, max: 20000, rate: 0.014 },
    { min: 20000, max: 35000, rate: 0.0175 },
    { min: 35000, max: 40000, rate: 0.035 },
    { min: 40000, max: 75000, rate: 0.05525 },
    { min: 75000, max: 500000, rate: 0.0637 },
    { min: 500000, max: 1000000, rate: 0.0897 },
    { min: 1000000, max: null, rate: 0.1075 }
  ]},
  NM: { name: 'New Mexico', type: 'progressive', brackets: [
    { min: 0, max: 5500, rate: 0.017 },
    { min: 5500, max: 11000, rate: 0.032 },
    { min: 11000, max: 16000, rate: 0.047 },
    { min: 16000, max: 210000, rate: 0.049 },
    { min: 210000, max: null, rate: 0.059 }
  ]},
  NY: { name: 'New York', type: 'progressive', brackets: [
    { min: 0, max: 8500, rate: 0.04 },
    { min: 8500, max: 11700, rate: 0.045 },
    { min: 11700, max: 13900, rate: 0.0525 },
    { min: 13900, max: 80650, rate: 0.055 },
    { min: 80650, max: 215400, rate: 0.06 },
    { min: 215400, max: 1077550, rate: 0.0685 },
    { min: 1077550, max: 5000000, rate: 0.0965 },
    { min: 5000000, max: 25000000, rate: 0.103 },
    { min: 25000000, max: null, rate: 0.109 }
  ]},
  NC: { name: 'North Carolina', type: 'flat', rate: 0.0475 },
  ND: { name: 'North Dakota', type: 'flat', rate: 0.0295 },
  OH: { name: 'Ohio', type: 'progressive', brackets: [
    { min: 0, max: 26050, rate: 0 },
    { min: 26050, max: 46100, rate: 0.0285 },
    { min: 46100, max: 92150, rate: 0.0333 },
    { min: 92150, max: 115300, rate: 0.0388 },
    { min: 115300, max: null, rate: 0.0399 }
  ]},
  OK: { name: 'Oklahoma', type: 'progressive', brackets: [
    { min: 0, max: 1000, rate: 0.0025 },
    { min: 1000, max: 2500, rate: 0.0075 },
    { min: 2500, max: 3750, rate: 0.0175 },
    { min: 3750, max: 4900, rate: 0.0275 },
    { min: 4900, max: 7200, rate: 0.0375 },
    { min: 7200, max: null, rate: 0.0475 }
  ]},
  OR: { name: 'Oregon', type: 'progressive', brackets: [
    { min: 0, max: 4050, rate: 0.0475 },
    { min: 4050, max: 10200, rate: 0.0675 },
    { min: 10200, max: 125000, rate: 0.0875 },
    { min: 125000, max: null, rate: 0.099 }
  ]},
  PA: { name: 'Pennsylvania', type: 'flat', rate: 0.0307 },
  RI: { name: 'Rhode Island', type: 'progressive', brackets: [
    { min: 0, max: 73450, rate: 0.0375 },
    { min: 73450, max: 166950, rate: 0.0475 },
    { min: 166950, max: null, rate: 0.0599 }
  ]},
  SC: { name: 'South Carolina', type: 'progressive', brackets: [
    { min: 0, max: 3200, rate: 0 },
    { min: 3200, max: 16040, rate: 0.03 },
    { min: 16040, max: null, rate: 0.064 }
  ]},
  SD: { name: 'South Dakota', type: 'none' },
  TN: { name: 'Tennessee', type: 'none' },
  TX: { name: 'Texas', type: 'none' },
  UT: { name: 'Utah', type: 'flat', rate: 0.0465 },
  VT: { name: 'Vermont', type: 'progressive', brackets: [
    { min: 0, max: 45400, rate: 0.0335 },
    { min: 45400, max: 110050, rate: 0.066 },
    { min: 110050, max: 229550, rate: 0.076 },
    { min: 229550, max: null, rate: 0.0875 }
  ]},
  VA: { name: 'Virginia', type: 'progressive', brackets: [
    { min: 0, max: 3000, rate: 0.02 },
    { min: 3000, max: 5000, rate: 0.03 },
    { min: 5000, max: 17000, rate: 0.05 },
    { min: 17000, max: null, rate: 0.0575 }
  ]},
  WA: { name: 'Washington', type: 'none' },
  WV: { name: 'West Virginia', type: 'progressive', brackets: [
    { min: 0, max: 10000, rate: 0.0236 },
    { min: 10000, max: 25000, rate: 0.0315 },
    { min: 25000, max: 40000, rate: 0.0354 },
    { min: 40000, max: 60000, rate: 0.0472 },
    { min: 60000, max: null, rate: 0.0512 }
  ]},
  WI: { name: 'Wisconsin', type: 'progressive', brackets: [
    { min: 0, max: 13810, rate: 0.0354 },
    { min: 13810, max: 27630, rate: 0.0465 },
    { min: 27630, max: 304170, rate: 0.0627 },
    { min: 304170, max: null, rate: 0.0765 }
  ]},
  WY: { name: 'Wyoming', type: 'none' }
};

export const TAX_YEARS: Record<number, TaxYearData> = {
  2024: {
    year: 2024,
    lastUpdated: '2024-01-01',
    standardDeductions: {
      single: 14600,
      marriedJoint: 29200,
      marriedSeparate: 14600,
      headOfHousehold: 21900,
    },
    brackets: {
      single: [
        { min: 0, max: 11600, rate: 0.10 },
        { min: 11600, max: 47150, rate: 0.12 },
        { min: 47150, max: 100525, rate: 0.22 },
        { min: 100525, max: 191950, rate: 0.24 },
        { min: 191950, max: 243725, rate: 0.32 },
        { min: 243725, max: 609350, rate: 0.35 },
        { min: 609350, max: null, rate: 0.37 },
      ],
      marriedJoint: [
        { min: 0, max: 23200, rate: 0.10 },
        { min: 23200, max: 94300, rate: 0.12 },
        { min: 94300, max: 201050, rate: 0.22 },
        { min: 201050, max: 383900, rate: 0.24 },
        { min: 383900, max: 487450, rate: 0.32 },
        { min: 487450, max: 731200, rate: 0.35 },
        { min: 731200, max: null, rate: 0.37 },
      ],
      marriedSeparate: [
        { min: 0, max: 11600, rate: 0.10 },
        { min: 11600, max: 47150, rate: 0.12 },
        { min: 47150, max: 100525, rate: 0.22 },
        { min: 100525, max: 191950, rate: 0.24 },
        { min: 191950, max: 243725, rate: 0.32 },
        { min: 243725, max: 365600, rate: 0.35 },
        { min: 365600, max: null, rate: 0.37 },
      ],
      headOfHousehold: [
        { min: 0, max: 16550, rate: 0.10 },
        { min: 16550, max: 63100, rate: 0.12 },
        { min: 63100, max: 100500, rate: 0.22 },
        { min: 100500, max: 191950, rate: 0.24 },
        { min: 191950, max: 243700, rate: 0.32 },
        { min: 243700, max: 609350, rate: 0.35 },
        { min: 609350, max: null, rate: 0.37 },
      ],
    },
  },
  2025: {
    year: 2025,
    lastUpdated: '2025-01-01',
    standardDeductions: {
      single: 15750,
      marriedJoint: 31500,
      marriedSeparate: 15750,
      headOfHousehold: 23625,
    },
    brackets: {
      single: [
        { min: 0, max: 11925, rate: 0.10 },
        { min: 11925, max: 48475, rate: 0.12 },
        { min: 48475, max: 103350, rate: 0.22 },
        { min: 103350, max: 197300, rate: 0.24 },
        { min: 197300, max: 250525, rate: 0.32 },
        { min: 250525, max: 626350, rate: 0.35 },
        { min: 626350, max: null, rate: 0.37 },
      ],
      marriedJoint: [
        { min: 0, max: 23850, rate: 0.10 },
        { min: 23850, max: 96950, rate: 0.12 },
        { min: 96950, max: 206700, rate: 0.22 },
        { min: 206700, max: 394600, rate: 0.24 },
        { min: 394600, max: 501050, rate: 0.32 },
        { min: 501050, max: 751600, rate: 0.35 },
        { min: 751600, max: null, rate: 0.37 },
      ],
      marriedSeparate: [
        { min: 0, max: 11925, rate: 0.10 },
        { min: 11925, max: 48475, rate: 0.12 },
        { min: 48475, max: 103350, rate: 0.22 },
        { min: 103350, max: 197300, rate: 0.24 },
        { min: 197300, max: 250525, rate: 0.32 },
        { min: 250525, max: 375800, rate: 0.35 },
        { min: 375800, max: null, rate: 0.37 },
      ],
      headOfHousehold: [
        { min: 0, max: 17000, rate: 0.10 },
        { min: 17000, max: 64850, rate: 0.12 },
        { min: 64850, max: 103350, rate: 0.22 },
        { min: 103350, max: 197300, rate: 0.24 },
        { min: 197300, max: 250500, rate: 0.32 },
        { min: 250500, max: 626350, rate: 0.35 },
        { min: 626350, max: null, rate: 0.37 },
      ],
    },
  },
};

export interface TaxCalculationInput {
  taxYear: number;
  filingStatus: 'single' | 'marriedJoint' | 'marriedSeparate' | 'headOfHousehold';
  grossIncome: number;
  additionalIncome: number;
  preTaxDeductions: number;
  postTaxDeductions: number;
  wages?: number;
  interest?: number;
  dividends?: number;
  businessIncome?: number;
  capitalGains?: number;
  otherIncome?: number;
  itemizedDeductions?: number;
  dependents?: number;
  childTaxCredit?: boolean;
  earnedIncomeCredit?: boolean;
  educationCredit?: number;
  retirementCredit?: number;
  otherCredits?: number;
  stateOfResidence?: string;
  federalTaxWithheld?: number;
}

export interface TaxCalculationResult {
  taxYear: number;
  filingStatus: string;
  grossIncome: number;
  additionalIncome: number;
  totalIncome: number;
  preTaxDeductions: number;
  adjustedGrossIncome: number;
  standardDeduction: number;
  itemizedDeductions: number;
  deductionUsed: number;
  taxableIncome: number;
  federalTaxLiability: number;
  effectiveTaxRate: number;
  marginalTaxRate: number;
  breakdown: {
    bracket: string;
    income: number;
    tax: number;
  }[];
  credits: {
    name: string;
    amount: number;
  }[];
  totalCredits: number;
  federalTaxOwed: number;
  federalTaxWithheld: number;
  federalRefundOrOwed: number;
  stateTax: number;
  stateOfResidence: string;
  finalTaxOwed: number;
}

// Document Extraction Interface
export interface ExtractedTaxData {
  documentType: string;
  wages?: number;
  federalWithheld?: number;
  preTaxDeductions?: number;
  interest?: number;
  dividends?: number;
  businessIncome?: number;
  otherIncome?: number;
  extractedFields: string[];
  warnings: string[];
}

// Secure Document Extraction Engine
export function extractTaxDocumentData(documentType: string, ocrText: string): ExtractedTaxData {
  const result: ExtractedTaxData = {
    documentType,
    extractedFields: [],
    warnings: []
  };

  // W-2 Extraction
  if (documentType === 'W2') {
    const box1Match = ocrText.match(/Box\s*1[:\s]+\$?([\d,]+\.?\d*)/i);
    const box2Match = ocrText.match(/Box\s*2[:\s]+\$?([\d,]+\.?\d*)/i);
    const box12Match = ocrText.match(/Box\s*12[:\s]+\$?([\d,]+\.?\d*)/i);

    if (box1Match) {
      result.wages = parseFloat(box1Match[1].replace(/,/g, ''));
      result.extractedFields.push('Wages (Box 1)');
    }
    if (box2Match) {
      result.federalWithheld = parseFloat(box2Match[1].replace(/,/g, ''));
      result.extractedFields.push('Federal Tax Withheld (Box 2)');
    }
    if (box12Match) {
      result.preTaxDeductions = parseFloat(box12Match[1].replace(/,/g, ''));
      result.extractedFields.push('Pre-Tax Deductions (Box 12)');
    }
  }

  // 1099-NEC Extraction
  if (documentType === '1099-NEC') {
    const box1Match = ocrText.match(/Box\s*1[:\s]+\$?([\d,]+\.?\d*)/i);
    if (box1Match) {
      result.businessIncome = parseFloat(box1Match[1].replace(/,/g, ''));
      result.extractedFields.push('Nonemployee Compensation (Box 1)');
    }
  }

  // 1099-INT Extraction
  if (documentType === '1099-INT') {
    const box1Match = ocrText.match(/Box\s*1[:\s]+\$?([\d,]+\.?\d*)/i);
    if (box1Match) {
      result.interest = parseFloat(box1Match[1].replace(/,/g, ''));
      result.extractedFields.push('Interest Income (Box 1)');
    }
  }

  // 1099-DIV Extraction
  if (documentType === '1099-DIV') {
    const box1aMatch = ocrText.match(/Box\s*1a[:\s]+\$?([\d,]+\.?\d*)/i);
    if (box1aMatch) {
      result.dividends = parseFloat(box1aMatch[1].replace(/,/g, ''));
      result.extractedFields.push('Ordinary Dividends (Box 1a)');
    }
  }

  // 1099-MISC Extraction
  if (documentType === '1099-MISC') {
    const box3Match = ocrText.match(/Box\s*3[:\s]+\$?([\d,]+\.?\d*)/i);
    if (box3Match) {
      result.otherIncome = parseFloat(box3Match[1].replace(/,/g, ''));
      result.extractedFields.push('Other Income (Box 3)');
    }
  }

  // K-1 Extraction
  if (documentType === 'K1') {
    const businessIncomeMatch = ocrText.match(/Ordinary\s+business\s+income[:\s]+\$?([\d,]+\.?\d*)/i);
    const interestMatch = ocrText.match(/Interest\s+income[:\s]+\$?([\d,]+\.?\d*)/i);
    const dividendMatch = ocrText.match(/Dividend\s+income[:\s]+\$?([\d,]+\.?\d*)/i);

    if (businessIncomeMatch) {
      result.businessIncome = parseFloat(businessIncomeMatch[1].replace(/,/g, ''));
      result.extractedFields.push('Ordinary Business Income');
    }
    if (interestMatch) {
      result.interest = parseFloat(interestMatch[1].replace(/,/g, ''));
      result.extractedFields.push('Interest Income');
    }
    if (dividendMatch) {
      result.dividends = parseFloat(dividendMatch[1].replace(/,/g, ''));
      result.extractedFields.push('Dividend Income');
    }
  }

  if (result.extractedFields.length === 0) {
    result.warnings.push('No income data could be extracted from this document. Please verify the document quality and try again.');
  }

  return result;
}

// Calculate State Tax
function calculateStateTax(taxableIncome: number, stateCode: string): number {
  const stateData = STATE_TAX_DATA[stateCode];
  if (!stateData || stateData.type === 'none') return 0;

  if (stateData.type === 'flat' && stateData.rate) {
    return taxableIncome * stateData.rate;
  }

  if (stateData.type === 'progressive' && stateData.brackets) {
    let stateTax = 0;
    let remainingIncome = taxableIncome;

    for (const bracket of stateData.brackets) {
      const bracketMax = bracket.max || Infinity;
      const incomeInBracket = Math.min(remainingIncome, Math.max(0, bracketMax - bracket.min));

      if (incomeInBracket > 0 && remainingIncome > 0) {
        stateTax += incomeInBracket * bracket.rate;
        remainingIncome -= incomeInBracket;
      }

      if (remainingIncome <= 0) break;
    }

    return stateTax;
  }

  return 0;
}

export function calculateTax(input: TaxCalculationInput): TaxCalculationResult {
  const yearData = TAX_YEARS[input.taxYear];
  if (!yearData) {
    throw new Error(`Tax year ${input.taxYear} not supported`);
  }

  const standardDeduction = yearData.standardDeductions[input.filingStatus];
  const brackets = yearData.brackets[input.filingStatus];
  
  // Calculate total income
  const totalIncome = input.grossIncome + input.additionalIncome;
  
  // Calculate AGI
  const adjustedGrossIncome = Math.max(0, totalIncome - input.preTaxDeductions);
  
  // Determine deduction to use
  const itemizedDeductions = input.itemizedDeductions || 0;
  const deductionUsed = Math.max(standardDeduction, itemizedDeductions);
  
  // Calculate taxable income
  const taxableIncome = Math.max(0, adjustedGrossIncome - deductionUsed);
  
  // Calculate federal tax using progressive brackets
  let federalTaxLiability = 0;
  const breakdown: { bracket: string; income: number; tax: number }[] = [];
  let remainingIncome = taxableIncome;
  
  for (let i = 0; i < brackets.length; i++) {
    const bracket = brackets[i];
    const bracketMax = bracket.max || Infinity;
    const incomeInBracket = Math.min(remainingIncome, Math.max(0, bracketMax - bracket.min));
    
    if (incomeInBracket > 0 && remainingIncome > 0) {
      const taxInBracket = incomeInBracket * bracket.rate;
      federalTaxLiability += taxInBracket;
      
      breakdown.push({
        bracket: `${(bracket.rate * 100).toFixed(0)}%`,
        income: incomeInBracket,
        tax: taxInBracket,
      });
      
      remainingIncome -= incomeInBracket;
    }
    
    if (remainingIncome <= 0) break;
  }
  
  // Calculate credits
  const credits: { name: string; amount: number }[] = [];
  let totalCredits = 0;
  
  if (input.childTaxCredit && input.dependents && input.dependents > 0) {
    const creditAmount = input.dependents * 2000;
    credits.push({ name: 'Child Tax Credit', amount: creditAmount });
    totalCredits += creditAmount;
  }
  
  if (input.earnedIncomeCredit && totalIncome < 60000) {
    const eicAmount = Math.min(7430, totalIncome * 0.05);
    credits.push({ name: 'Earned Income Credit', amount: eicAmount });
    totalCredits += eicAmount;
  }

  if (input.educationCredit && input.educationCredit > 0) {
    credits.push({ name: 'Education Credit', amount: input.educationCredit });
    totalCredits += input.educationCredit;
  }

  if (input.retirementCredit && input.retirementCredit > 0) {
    credits.push({ name: 'Retirement Savings Credit', amount: input.retirementCredit });
    totalCredits += input.retirementCredit;
  }

  if (input.otherCredits && input.otherCredits > 0) {
    credits.push({ name: 'Other Credits', amount: input.otherCredits });
    totalCredits += input.otherCredits;
  }
  
  const federalTaxOwed = Math.max(0, federalTaxLiability - totalCredits);
  
  // Calculate state tax
  const stateCode = input.stateOfResidence || 'CA';
  const stateTax = calculateStateTax(taxableIncome, stateCode);
  
  // Calculate refund or amount owed
  const federalWithheld = input.federalTaxWithheld || 0;
  const federalRefundOrOwed = federalWithheld - federalTaxOwed;
  
  // Calculate final tax owed (federal + state)
  const finalTaxOwed = federalTaxOwed + stateTax;
  
  // Calculate rates
  const effectiveTaxRate = totalIncome > 0 ? (federalTaxOwed / totalIncome) * 100 : 0;
  const marginalBracket = brackets.find(b => 
    taxableIncome >= b.min && (b.max === null || taxableIncome <= b.max)
  );
  const marginalTaxRate = marginalBracket ? marginalBracket.rate * 100 : 0;
  
  return {
    taxYear: input.taxYear,
    filingStatus: input.filingStatus,
    grossIncome: input.grossIncome,
    additionalIncome: input.additionalIncome,
    totalIncome,
    preTaxDeductions: input.preTaxDeductions,
    adjustedGrossIncome,
    standardDeduction,
    itemizedDeductions,
    deductionUsed,
    taxableIncome,
    federalTaxLiability,
    effectiveTaxRate,
    marginalTaxRate,
    breakdown,
    credits,
    totalCredits,
    federalTaxOwed,
    federalTaxWithheld: federalWithheld,
    federalRefundOrOwed,
    stateTax,
    stateOfResidence: STATE_TAX_DATA[stateCode]?.name || 'Unknown',
    finalTaxOwed,
  };
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatPercentage(rate: number): string {
  return `${rate.toFixed(2)}%`;
}
